import { OnboardingStatusService } from "./OnboardingStatusApi";
import { failure, isOk, ok, pending, Result } from "../common/common";
import {
  OnboardingError,
  OnboardingSaveStatus,
  OnboardingStatus,
  OnboardingStatusResponse,
  shareholderErrors,
} from "./OnboardingStatusDomain";
import { logDebug } from "../utils/log";
import { makeAutoObservable } from "mobx";
import { CompanyRole } from "../types/CompanyInfo";
import { ExtendedShareholderDTO } from "../types/Shareholder";
import { isAxiosError } from "../api";

import { TMessage } from "../i18n/tt";
import { AccessType } from "../types/enums/AccessType";

export class OnboardingStatusController {
  private api: OnboardingStatusService;
  private readonly organisationNumber: string;
  onboardingSteps: Result<OnboardingStatusResponse> = pending();
  private onboardingSaveStatus: OnboardingSaveStatus = OnboardingSaveStatus.IDLE;
  validRoles = true;
  missingShareholders = false;
  serverValidationErrors: OnboardingError[] = [];
  companyRolesOnboardingComplete: Result<void, TMessage> = pending();
  shareholdersOnboardingComplete: Result<void, TMessage> = pending();
  articlesOfAssociationOnboardingComplete: Result<void, TMessage> = pending();
  accessType: AccessType | null;

  constructor(
    api: OnboardingStatusService,
    organisationNumber: string,
    accessType: AccessType | null,
  ) {
    this.api = api;
    this.organisationNumber = organisationNumber;
    this.accessType = accessType;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  *loadOnboardingStatus() {
    try {
      if (this.accessType !== AccessType.SHAREHOLDER) {
        const result: OnboardingStatusResponse = yield this.api.getOnboardingStatus(
          this.organisationNumber,
        );
        this.onboardingSteps = ok(result);
      }
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 404) {
        logDebug(`No onboarding status available for ${this.organisationNumber}`);
      } else {
        this.onboardingSteps = failure();
      }
    }
  }

  *onboardArticlesOfAssociation() {
    try {
      this.onboardingSaveStatus = OnboardingSaveStatus.LOADING;
      const onboardedResult: void = yield this.api.onboardArticlesOfAssociation(
        this.organisationNumber,
      );
      this.articlesOfAssociationOnboardingComplete = ok(onboardedResult);
      yield this.loadOnboardingStatus();
    } catch (e) {
      this.articlesOfAssociationOnboardingComplete = failure({
        key: "onboarding.step.error.confirm",
      });
    } finally {
      this.onboardingSaveStatus = OnboardingSaveStatus.IDLE;
    }
  }

  *onboardCompanyRoles(roles: CompanyRole[]) {
    try {
      this.validRoles = hasValidRoles(roles);
      if (this.validRoles) {
        this.onboardingSaveStatus = OnboardingSaveStatus.LOADING;
        const onboardedResult: void = yield this.api.onboardCompanyRoles(this.organisationNumber);
        this.companyRolesOnboardingComplete = ok(onboardedResult);
        yield this.loadOnboardingStatus();
      }
    } catch (e) {
      this.companyRolesOnboardingComplete = failure({ key: "onboarding.step.error.confirm" });
    } finally {
      this.onboardingSaveStatus = OnboardingSaveStatus.IDLE;
    }
  }

  *onboardShareholders(shareholders: ExtendedShareholderDTO[]) {
    this.serverValidationErrors = [];

    try {
      this.missingShareholders = shareholders.length === 0;
      if (!this.missingShareholders) {
        this.onboardingSaveStatus = OnboardingSaveStatus.LOADING;
        const onboardedResult: void = yield this.api.onboardShareholders(this.organisationNumber);
        this.shareholdersOnboardingComplete = ok(onboardedResult);
        yield this.loadOnboardingStatus();
      }
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === 400 &&
        Array.isArray(error.response.data)
      ) {
        const errors: Array<{ validationError: string; context: string }> = error.response.data;
        this.serverValidationErrors = errors.map((error) => {
          return {
            validationError:
              shareholderErrors.get(error.validationError) ??
              "onboarding.step.error.confirm.unknown",
            context: error.context,
          };
        });
      } else {
        this.shareholdersOnboardingComplete = failure({ key: "onboarding.step.error.confirm" });
      }
    } finally {
      this.onboardingSaveStatus = OnboardingSaveStatus.IDLE;
    }
  }

  *markShareholdersIncompleteIfValidationErrors() {
    if (
      isOk(this.onboardingSteps) &&
      this.onboardingSteps.data.shareholderRegister === OnboardingStatus.complete
    ) {
      yield this.api.markShareholdersIncomplete(this.organisationNumber);
      this.shareholdersOnboardingComplete = pending();
      this.onboardingSteps.data.shareholderRegister = OnboardingStatus.incomplete;
    }
  }

  get renderIncompleteBoardRoles(): boolean {
    return (
      isOk(this.onboardingSteps) &&
      this.onboardingSteps.data.boardMembers === OnboardingStatus.incomplete
    );
  }

  get renderIncompleteShareholders(): boolean {
    return (
      isOk(this.onboardingSteps) &&
      this.onboardingSteps.data.shareholderRegister === OnboardingStatus.incomplete
    );
  }

  get renderIncompleteArticlesOfAssociation(): boolean {
    return (
      isOk(this.onboardingSteps) &&
      this.onboardingSteps.data.articlesOfAssociation === OnboardingStatus.incomplete
    );
  }

  get isSaveInProgress(): boolean {
    return this.onboardingSaveStatus === OnboardingSaveStatus.LOADING;
  }

  get renderSuccessfulCompanyRolesOnboarding(): boolean {
    return isOk(this.companyRolesOnboardingComplete);
  }

  get renderSuccessfulShareholdersOnboarding(): boolean {
    return isOk(this.shareholdersOnboardingComplete);
  }

  get renderSuccessfulArticlesOfAssociationOnboarding(): boolean {
    return isOk(this.articlesOfAssociationOnboardingComplete);
  }

  clearValidationErrors() {
    this.validRoles = true;
  }
}

export function hasValidRoles(roles: CompanyRole[]): boolean {
  if (roles.length === 0) return false;
  return roles.filter((it) => it.email === undefined).length === 0;
}
